import React, { useState } from 'react';
import { Clock, User } from 'lucide-react';
import { schedule } from '../data/mock';

const Schedule = () => {
  const [selectedModality, setSelectedModality] = useState('Todas');
  const [selectedDay, setSelectedDay] = useState('monday');

  const days = [
    { key: 'monday', label: 'Segunda' },
    { key: 'tuesday', label: 'Terça' },
    { key: 'wednesday', label: 'Quarta' },
    { key: 'thursday', label: 'Quinta' },
    { key: 'friday', label: 'Sexta' },
    { key: 'saturday', label: 'Sábado' }
  ];

  const filterOptions = [
    'Todas', 'Muay Thai', 'Jiujitsu', 'Boxe', 'Kickfit', 'Kids', 'Ginástica Rítmica'
  ];

  const filterClass = (classItem) => {
    if (selectedModality === 'Todas') return true;
    const name = classItem.class.toLowerCase();
    const filter = selectedModality.toLowerCase();
    if (filter === 'kids') return name.includes('kids') || name.includes('judo');
    if (filter === 'muay thai') return name.includes('muay thai') || name.includes('kickboxing');
    return name.includes(filter);
  };

  const getBgImage = (className) => {
    const name = className.toLowerCase();
    if (name.includes('muay thai') || name.includes('kickboxing')) return 'muay_thai_silhouette_bg_1774631485469.png';
    if (name.includes('jiujitsu')) return 'jiujitsu_roll_subtle_bg_1774631500408.png';
    if (name.includes('boxe')) return 'boxing_bag_subtle_bg_1774631516829.png';
    if (name.includes('kickfit')) return 'kickfit_subtle_bg_v2_1774632275520.png';
    if (name.includes('kids') || name.includes('judo')) return 'kids_martial_arts_subtle_bg_v2_1774631577085.png';
    if (name.includes('rítmica')) return 'rhythmic_gymnastics_subtle_bg_v2_1774631559233.png';
    return 'subtle_gym_texture_bg_1774630285219.png';
  };

  const filteredClasses = (schedule[selectedDay] || []).filter(filterClass);

  return (
    <section id="horarios" className="py-24 bg-bg-page relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-brand-primary/5 blur-[120px] pointer-events-none"></div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="heading-2 text-brand-primary mb-4">Agenda Interativa</h2>
          <p className="body-medium text-gray-400 max-w-3xl mx-auto mb-10">
            Filtre por sua modalidade preferida e encontre o melhor horário para treinar.
          </p>

          {/* Modality Filter Chips */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {filterOptions.map((modality) => (
              <button
                key={modality}
                onClick={() => setSelectedModality(modality)}
                className={`px-5 py-2 rounded-full text-sm font-bold transition-all duration-300 border ${
                  selectedModality === modality
                    ? 'bg-brand-primary text-white border-brand-primary shadow-lg shadow-brand-primary/30'
                    : 'bg-bg-card text-gray-400 border-border-medium hover:border-brand-primary hover:text-white'
                }`}
              >
                {modality.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Day Selector */}
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 mb-10 bg-bg-card border border-border-medium rounded-xl p-2">
          {days.map((day) => (
            <button
              key={day.key}
              onClick={() => setSelectedDay(day.key)}
              className={`py-3 rounded-lg text-xs font-bold uppercase tracking-widest transition-all duration-300 ${
                selectedDay === day.key
                  ? 'bg-brand-primary text-white shadow-lg shadow-brand-primary/30'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {day.label}
            </button>
          ))}
        </div>

        {/* Classes Grid */}
        {filteredClasses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClasses.map((classItem, index) => (
              <div
                key={index}
                className="relative glass-card border-white/5 rounded-2xl p-6 hover:border-brand-primary group overflow-hidden min-h-[160px] flex flex-col justify-between transition-all duration-300"
              >
                <div
                  className="absolute inset-0 opacity-10 group-hover:opacity-25 transition-opacity duration-500 bg-cover bg-center grayscale"
                  style={{ backgroundImage: `url('${getBgImage(classItem.class)}')` }}
                ></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-5 h-5 text-brand-primary" />
                      <span className="text-xl text-brand-primary font-black">{classItem.time}</span>
                    </div>
                    <div className="bg-brand-primary/10 border border-brand-primary/20 rounded px-2 py-1">
                      <span className="text-[10px] text-brand-primary font-bold uppercase tracking-tighter">{classItem.level}</span>
                    </div>
                  </div>
                  <h3 className="text-2xl font-black text-white mb-4 leading-tight">{classItem.class}</h3>
                  <div className="flex items-center space-x-2 pt-4 border-t border-white/5">
                    <User className="w-4 h-4 text-brand-primary" />
                    <span className="text-xs text-gray-400 font-bold uppercase tracking-wide">{classItem.instructor}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-20 text-center bg-bg-card rounded-3xl border border-dashed border-border-medium">
            <p className="text-xl text-gray-500 font-medium italic">
              Nenhuma aula nesta modalidade nesta data.
            </p>
          </div>
        )}

        <div className="mt-10 text-center">
          <p className="body-small text-gray-500">
            * Horários sujeitos a alteração. Entre em contato para confirmar disponibilidade.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Schedule;
